import { useState, useEffect, useCallback, useMemo } from "react"

const STORAGE_KEY = "tree_filters"

const DEFAULT_FILTERS = {
  scored: false,
  open: false,
  guests: false,
  search: "",
  selectedNodeIds: [],
}

export const useTreeFilters = () => {
  const [appliedFilters, setAppliedFilters] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored
        ? { ...DEFAULT_FILTERS, ...JSON.parse(stored) }
        : DEFAULT_FILTERS
    } catch {
      return DEFAULT_FILTERS
    }
  })

  const [pendingFilters, setPendingFilters] = useState(appliedFilters)

  useEffect(() => {
    setPendingFilters(appliedFilters)
  }, [appliedFilters])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(appliedFilters))
    } catch (error) {
      console.error("Failed to save filters:", error)
    }
  }, [appliedFilters])

  const updatePendingFilter = useCallback((key, value) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const applyFilters = useCallback(() => {
    setAppliedFilters(pendingFilters)
  }, [pendingFilters])

  const setOnlyFilter = useCallback((filterKey, currentSearch = "") => {
    const newFilters = {
      scored: false,
      open: false,
      guests: false,
      [filterKey]: true,
      search: currentSearch,
      selectedNodeIds: [],
    }
    setPendingFilters(newFilters)
    setAppliedFilters(newFilters)
  }, [])

  const setOnlyNodeFilter = useCallback((nodeId) => {
    const newFilters = {
      ...DEFAULT_FILTERS,
      selectedNodeIds: [nodeId],
    }
    setPendingFilters(newFilters)
    setAppliedFilters(newFilters)
  }, [])

  const resetFilters = useCallback(() => {
    setPendingFilters(DEFAULT_FILTERS)
    setAppliedFilters(DEFAULT_FILTERS)
  }, [])

  const hasActiveFilters = useMemo(() => {
    return Object.entries(appliedFilters).some(([key, value]) => {
      if (key === "search") {
        return value.length > 0
      }
      if (key === "selectedNodeIds") {
        return Array.isArray(value) && value.length > 0
      }
      return value !== DEFAULT_FILTERS[key]
    })
  }, [appliedFilters])

  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters)
  }, [pendingFilters, appliedFilters])

  return {
    filters: appliedFilters,
    pendingFilters,
    updatePendingFilter,
    applyFilters,
    setOnlyFilter,
    setOnlyNodeFilter,
    resetFilters,
    hasActiveFilters,
    hasPendingChanges,
  }
}
